/**
 * The one validation gate in front of Test, Load databases, Save and Save anyway (Option C, C0, plan §3.1):
 * 1. reconcile — clear the errors of fields that do not apply to the values on screen;
 * 2. `validateAllFields('submit')` — the rule validators answer nothing for inapplicable fields;
 * 3. pick the first blocking issue: the first applicable field error in screen order, else a form issue;
 * 4. open its section, scroll to and focus it (with the "Press Tab to fill" hint when Tab has a default for the empty field); a form
 *    issue also shows its warning in the results area — but "tick a service", which its own row always says under the
 *    checkboxes (final UX review UX-04: one placement).
 * Save runs the gate BEFORE `form.handleSubmit()`, so TanStack's own `canSubmit` check never sees a stale error of a
 * mode the user left (B8). Callers capture their Save authorization before calling it (its first await).
 */
import { useState } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import { firstBlockingIssue, type BlockingIssue } from '../state/blockingIssue';
import { reconcileFieldMeta } from '../state/reconcileFieldMeta';
import type { ConnectionForm } from '../state/connectionForm';
import { revealControl } from '../pane/formSectionReveal';

type Options = { form: ConnectionForm; tests: ReturnType<typeof useConnectionTestOutcome>; track: TrackFunction };

export type DefaultHint = { field: string; value: string };

function focusControl(selector: string): void {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) return;
  // A collapsed section opens first (its fields stay mounted, C2); focus without the browser's jump-scroll, then bring
  // the control to the centre of the dialog's scroll container.
  revealControl(element, () => {
    element.focus({ preventScroll: true });
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
}

export function useValidationGate({ form, tests, track }: Options) {
  const [defaultHint, setDefaultHint] = useState<DefaultHint | null>(null);

  const present = (issue: BlockingIssue) => {
    focusControl(issue.selector);
    if (issue.kind === 'form') {
      if (issue.id !== 'storage-services') tests.show(tests.snapshotOf(), { result: { success: false, variant: 'warning', message: issue.message } });
      return;
    }
    setDefaultHint(issue.isEmpty && issue.defaultValue ? { field: issue.field, value: issue.defaultValue } : null);
    track('feature:connection_validation_focus', { engine: form.state.values.engine, field: issue.field, has_default: !!issue.defaultValue });
  };

  const runGate = async (): Promise<boolean> => {
    reconcileFieldMeta(form);
    await form.validateAllFields('submit');
    reconcileFieldMeta(form);
    const issue = firstBlockingIssue(form.state.fieldMeta, form.state.values);
    if (!issue) return true;
    present(issue);
    return false;
  };

  return { runGate, defaultHint, dismissDefaultHint: () => setDefaultHint(null) };
}
